export interface SignTextSide {
  lines: [string, string, string, string];
  color: string;
  glowing: boolean;
}

export interface SignBlockEntityData {
  front: SignTextSide;
  back: SignTextSide;
  waxed: boolean;
}

export interface BannerPatternData {
  pattern: string;
  color: string;
}

export interface BannerBlockEntityData {
  patterns: BannerPatternData[];
}

export interface ContainerItemData {
  slot: number;
  id: string;
  count: number;
  componentsSnbt?: string;
  legacyTagSnbt?: string;
}

export interface ContainerBlockEntityData {
  items: ContainerItemData[];
  customName?: string;
  lock?: string;
  lootTable?: string;
}

export interface PlayerHeadBlockEntityData {
  name?: string;
  uuid?: string;
  textureValue?: string;
  textureUrl?: string;
}

export interface BlockEntityData {
  sign?: SignBlockEntityData;
  banner?: BannerBlockEntityData;
  container?: ContainerBlockEntityData;
  playerHead?: PlayerHeadBlockEntityData;
  customName?: string;
  rawSnbt?: string;
}

const DYE_TO_LEGACY = {
  white: 0,
  orange: 1,
  magenta: 2,
  light_blue: 3,
  yellow: 4,
  lime: 5,
  pink: 6,
  gray: 7,
  light_gray: 8,
  cyan: 9,
  purple: 10,
  blue: 11,
  brown: 12,
  green: 13,
  red: 14,
  black: 15
};

const MODERN_TO_LEGACY_PATTERN = {
  base: "b",
  square_bottom_left: "bl",
  square_bottom_right: "br",
  square_top_left: "tl",
  square_top_right: "tr",
  stripe_bottom: "bs",
  stripe_top: "ts",
  stripe_left: "ls",
  stripe_right: "rs",
  stripe_center: "cs",
  stripe_middle: "ms",
  stripe_downright: "drs",
  stripe_downleft: "dls",
  small_stripes: "ss",
  cross: "cr",
  straight_cross: "sc",
  triangle_bottom: "bt",
  triangle_top: "tt",
  triangles_bottom: "bts",
  triangles_top: "tts",
  diagonal_left: "ld",
  diagonal_up_right: "rd",
  diagonal_up_left: "lud",
  diagonal_right: "rud",
  circle: "mc",
  rhombus: "mr",
  half_vertical: "vh",
  half_horizontal: "hh",
  half_vertical_right: "vhr",
  half_horizontal_bottom: "hhb",
  border: "bo",
  curly_border: "cbo",
  gradient: "gra",
  gradient_up: "gru",
  bricks: "bri",
  globe: "glb",
  creeper: "cre",
  skull: "sku",
  flower: "flo",
  mojang: "moj",
  piglin: "pig"
};

function cloneValue<T>(value: T): T {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

export function cloneBlockEntityData(
  data: BlockEntityData | null | undefined
): BlockEntityData | null {
  return data ? cloneValue(data) : null;
}

export function createDefaultBlockEntityData(
  blockId: string
): BlockEntityData | null {
  if (
    blockId.endsWith("_sign") ||
    blockId.endsWith("_hanging_sign")
  ) {
    const side = (): SignTextSide => ({
      lines: ["", "", "", ""],
      color: "black",
      glowing: false
    });
    return {
      sign: {
        front: side(),
        back: side(),
        waxed: false
      }
    };
  }

  if (
    blockId.endsWith("_banner") ||
    blockId.endsWith("_wall_banner")
  ) {
    return {
      banner: {
        patterns: []
      }
    };
  }

  if (
    blockId.includes("chest") ||
    blockId.endsWith("_shulker_box") ||
    blockId === "barrel" ||
    blockId === "hopper" ||
    blockId === "dispenser" ||
    blockId === "dropper"
  ) {
    return {
      container: {
        items: []
      }
    };
  }

  if (
    blockId === "player_head" ||
    blockId === "player_wall_head"
  ) {
    return {
      playerHead: {}
    };
  }

  return null;
}

export function inferBlockEntityEditorKind(
  blockId: string
): "sign" | "banner" | "container" | "player_head" | null {
  if (
    blockId.endsWith("_sign") ||
    blockId.endsWith("_hanging_sign")
  ) return "sign";
  if (
    blockId.endsWith("_banner") ||
    blockId.endsWith("_wall_banner")
  ) return "banner";
  if (
    blockId === "player_head" ||
    blockId === "player_wall_head"
  ) return "player_head";
  if (
    blockId.includes("chest") ||
    blockId.endsWith("_shulker_box") ||
    blockId === "barrel" ||
    blockId === "hopper" ||
    blockId === "dispenser" ||
    blockId === "dropper"
  ) return "container";
  return null;
}

function quote(value: string): string {
  return JSON.stringify(String(value));
}

function normalizeId(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "minecraft:air";
  return trimmed.includes(":") ? trimmed : "minecraft:" + trimmed;
}

function parseVersion(versionId: string | null | undefined): number[] {
  return String(versionId || "0")
    .split(/[.-]/)
    .map((part) => Number.parseInt(part, 10))
    .map((value) => Number.isFinite(value) ? value : 0);
}

export function isMinecraftVersionAtLeast(
  versionId: string | null | undefined,
  target: string
): boolean {
  const current = parseVersion(versionId);
  const minimum = parseVersion(target);
  const length = Math.max(current.length, minimum.length);

  for (let index = 0; index < length; index += 1) {
    const a = current[index] || 0;
    const b = minimum[index] || 0;
    if (a > b) return true;
    if (a < b) return false;
  }

  return true;
}

function modernTextComponent(text: string): string {
  return "{text:" + quote(text) + "}";
}

function legacyJsonTextComponent(text: string): string {
  return quote(JSON.stringify({ text }));
}

function signSideToSnbt(
  side: SignTextSide,
  directComponents: boolean
): string {
  const messages = side.lines.map((line) =>
    directComponents
      ? modernTextComponent(line)
      : legacyJsonTextComponent(line)
  );

  return (
    "{messages:[" +
    messages.join(",") +
    "],color:" +
    quote(side.color || "black") +
    ",has_glowing_text:" +
    (side.glowing ? "1b" : "0b") +
    "}"
  );
}

function signToSnbt(
  sign: SignBlockEntityData,
  versionId: string | null
): string {
  if (!isMinecraftVersionAtLeast(versionId, "1.20")) {
    return sign.front.lines
      .map((line, index) =>
        "Text" + (index + 1) + ":" + legacyJsonTextComponent(line)
      )
      .join(",");
  }

  const directComponents =
    isMinecraftVersionAtLeast(versionId, "1.21.5");

  return [
    "front_text:" + signSideToSnbt(sign.front, directComponents),
    "back_text:" + signSideToSnbt(sign.back, directComponents),
    "is_waxed:" + (sign.waxed ? "1b" : "0b")
  ].join(",");
}

function bannerToSnbt(
  banner: BannerBlockEntityData,
  versionId: string | null
): string {
  if (isMinecraftVersionAtLeast(versionId, "1.20.5")) {
    return (
      "patterns:[" +
      banner.patterns.map((pattern) => {
        return (
          "{pattern:" +
          quote(normalizeId(pattern.pattern)) +
          ",color:" +
          quote(pattern.color) +
          "}"
        );
      }).join(",") +
      "]"
    );
  }

  return (
    "Patterns:[" +
    banner.patterns.map((pattern) => {
      const modernName = pattern.pattern.replace(/^minecraft:/, "");
      const legacyPattern =
        MODERN_TO_LEGACY_PATTERN[modernName] || modernName;
      const legacyColor =
        DYE_TO_LEGACY[pattern.color] ?? DYE_TO_LEGACY.white;

      return (
        "{Pattern:" +
        quote(legacyPattern) +
        ",Color:" +
        legacyColor +
        "b}"
      );
    }).join(",") +
    "]"
  );
}

function itemToSnbt(
  item: ContainerItemData,
  versionId: string | null
): string {
  const slot = Math.max(0, Math.min(255, Math.round(item.slot)));
  const count = Math.max(1, Math.round(item.count || 1));
  const id = normalizeId(item.id);

  if (isMinecraftVersionAtLeast(versionId, "1.20.5")) {
    const fields = [
      "Slot:" + slot + "b",
      "id:" + quote(id),
      "count:" + count
    ];
    if (item.componentsSnbt?.trim()) {
      fields.push("components:" + item.componentsSnbt.trim());
    }
    return "{" + fields.join(",") + "}";
  }

  const fields = [
    "Slot:" + slot + "b",
    "id:" + quote(id),
    "Count:" + Math.min(127, count) + "b"
  ];
  if (item.legacyTagSnbt?.trim()) {
    fields.push("tag:" + item.legacyTagSnbt.trim());
  }
  return "{" + fields.join(",") + "}";
}

function containerToSnbt(
  container: ContainerBlockEntityData,
  versionId: string | null
): string {
  const fields = [
    "Items:[" +
      container.items
        .slice()
        .sort((a, b) => a.slot - b.slot)
        .map((item) => itemToSnbt(item, versionId))
        .join(",") +
      "]"
  ];

  if (container.customName) {
    fields.push(
      "CustomName:" +
        quote(JSON.stringify({ text: container.customName }))
    );
  }
  if (container.lock) {
    fields.push("lock:" + quote(container.lock));
  }
  if (container.lootTable) {
    fields.push("LootTable:" + quote(normalizeId(container.lootTable)));
  }

  return fields.join(",");
}

function uuidToIntArray(uuid: string): string | null {
  const hex = uuid.replace(/-/g, "").toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(hex)) return null;

  const ints: string[] = [];
  for (let index = 0; index < 32; index += 8) {
    const unsigned = Number.parseInt(hex.slice(index, index + 8), 16);
    const signed = unsigned > 0x7fffffff
      ? unsigned - 0x100000000
      : unsigned;
    ints.push(String(signed));
  }

  return "[I;" + ints.join(",") + "]";
}

export function encodeSkinTextureUrl(textureUrl: string): string {
  const payload = JSON.stringify({
    textures: {
      SKIN: {
        url: textureUrl
      }
    }
  });

  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  let output = "";

  for (let index = 0; index < payload.length; index += 3) {
    const byte1 = payload.charCodeAt(index) & 0xff;
    const hasByte2 = index + 1 < payload.length;
    const hasByte3 = index + 2 < payload.length;
    const byte2 = hasByte2 ? payload.charCodeAt(index + 1) & 0xff : 0;
    const byte3 = hasByte3 ? payload.charCodeAt(index + 2) & 0xff : 0;

    const chunk = (byte1 << 16) | (byte2 << 8) | byte3;
    output += alphabet[(chunk >> 18) & 63];
    output += alphabet[(chunk >> 12) & 63];
    output += hasByte2 ? alphabet[(chunk >> 6) & 63] : "=";
    output += hasByte3 ? alphabet[chunk & 63] : "=";
  }

  return output;
}

export function extractSkinTextureUrl(
  textureValue: string | null | undefined
): string | null {
  const value = String(textureValue || "").trim();
  if (!value) return null;

  try {
    const decoded = globalThis.atob(value);
    const parsed = JSON.parse(decoded);
    const url = parsed?.textures?.SKIN?.url;
    return typeof url === "string" && url ? url : null;
  } catch {
    return null;
  }
}

function playerHeadToSnbt(
  head: PlayerHeadBlockEntityData,
  versionId: string | null
): string {
  const textureValue =
    head.textureValue ||
    (head.textureUrl ? encodeSkinTextureUrl(head.textureUrl) : "");

  if (isMinecraftVersionAtLeast(versionId, "1.20.5")) {
    const profileFields: string[] = [];

    if (head.name) {
      profileFields.push("name:" + quote(head.name));
    }

    const idArray = head.uuid ? uuidToIntArray(head.uuid) : null;
    if (idArray) {
      profileFields.push("id:" + idArray);
    }

    if (textureValue) {
      profileFields.push(
        "properties:[{name:" +
          quote("textures") +
          ",value:" +
          quote(textureValue) +
          "}]"
      );
    }

    return "profile:{" + profileFields.join(",") + "}";
  }

  const legacyFields: string[] = [];
  if (head.name) legacyFields.push("Name:" + quote(head.name));
  if (textureValue) {
    legacyFields.push(
      "Properties:{textures:[{Value:" + quote(textureValue) + "}]}"
    );
  }

  return "SkullOwner:{" + legacyFields.join(",") + "}";
}

export function serializeStructuredBlockEntitySnbt(
  data: BlockEntityData | null | undefined,
  versionId: string | null
): string | null {
  if (!data) return null;

  const fields: string[] = [];

  if (data.sign) {
    fields.push(signToSnbt(data.sign, versionId));
  }
  if (data.banner) {
    fields.push(bannerToSnbt(data.banner, versionId));
  }
  if (data.container) {
    fields.push(containerToSnbt(data.container, versionId));
  }
  if (data.playerHead) {
    fields.push(playerHeadToSnbt(data.playerHead, versionId));
  }
  if (data.customName) {
    fields.push(
      "CustomName:" +
        quote(JSON.stringify({ text: data.customName }))
    );
  }

  if (fields.length === 0) return null;
  return "{" + fields.join(",") + "}";
}

export function normalizeBlockEntityData(
  data: BlockEntityData | null | undefined
): BlockEntityData | null {
  if (!data) return null;

  const normalized = cloneBlockEntityData(data);
  if (!normalized) return null;

  if (normalized.sign) {
    const normalizeSide = (side: SignTextSide): SignTextSide => ({
      lines: [
        String(side.lines?.[0] || ""),
        String(side.lines?.[1] || ""),
        String(side.lines?.[2] || ""),
        String(side.lines?.[3] || "")
      ],
      color: String(side.color || "black"),
      glowing: Boolean(side.glowing)
    });

    normalized.sign = {
      front: normalizeSide(normalized.sign.front),
      back: normalizeSide(normalized.sign.back),
      waxed: Boolean(normalized.sign.waxed)
    };
  }

  if (normalized.banner) {
    normalized.banner.patterns = (normalized.banner.patterns || [])
      .slice(0, 20)
      .map((pattern) => ({
        pattern: normalizeId(pattern.pattern),
        color: String(pattern.color || "white")
      }));
  }

  if (normalized.container) {
    normalized.container.items = (normalized.container.items || [])
      .map((item) => ({
        ...item,
        slot: Math.max(0, Math.min(255, Math.round(item.slot))),
        id: normalizeId(item.id),
        count: Math.max(1, Math.round(item.count || 1))
      }));
  }

  return normalized;
}
